import { useState, useEffect, useCallback, useRef, FC } from 'react';
import {
  Users,
  Plus,
  RefreshCw,
  Link2,
  ExternalLink,
  Edit2,
  Trash2,
  Radio,
  Check,
  Copy,
  LayoutGrid,
  List,
  BarChart2,
  Smartphone,
  MousePointerClick,
  Send,
  ChevronDown,
  Info,
  Clock,
  AlertCircle,
  Search,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../ui/Toast';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { Input } from '../ui/Input';
import { ConfirmationModal } from '../ui/ConfirmationModal';
import { Drawer } from '../ui/Drawer';
import { Tooltip } from '../ui/Tooltip';
import { LoadingSkeleton } from '../ui/LoadingSkeleton';
import { PageHeader } from '../widgets/PageHeader';
import { DataTable, Column } from '../widgets/DataTable';
import {
  fetchGroups,
  createGroup,
  updateGroup,
  deleteGroup,
  syncGroup,
  syncAllGroups,
  clearAllGroups,
  relinkGroup,
  fetchAvailableGroups,
  resolveGroupLink,
  resolveGroupJid,
  AvailableWhatsAppGroup,
  WhatsAppGroup,
  WhatsAppGroupCreatePayload,
} from '../../api/groups';
import { GroupMetricsDrawer } from './GroupMetricsDrawer';
import { GroupMonitorView } from './GroupMonitorView';
import { MirrorGroupView } from './MirrorGroupView';

export interface GroupsViewProps {
  initialDrawerOpen?: boolean;
  initialGroups?: WhatsAppGroup[];
  onGroupsChange?: (groups: WhatsAppGroup[]) => void;
  isWhatsAppConnected?: boolean;
  initialSubTab?: 'list' | 'monitor' | 'mirror';
}

export const GroupsView: FC<GroupsViewProps> = ({
  initialDrawerOpen = false,
  initialGroups = [],
  onGroupsChange,
  isWhatsAppConnected = true,
  initialSubTab,
}) => {
  const { token } = useAuth();
  const { success, error, info } = useToast();

  const [groups, setGroups] = useState<WhatsAppGroup[]>(initialGroups);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isSyncingAll, setIsSyncingAll] = useState(false);
  const [syncingGroupId, setSyncingGroupId] = useState<string | null>(null);
  const [togglingGroupId, setTogglingGroupId] = useState<string | null>(null);

  // Sub-tab switcher: list vs monitor vs mirror
  const [subTab, setSubTab] = useState<'list' | 'monitor' | 'mirror'>(() => {
    if (initialSubTab) return initialSubTab;
    if (typeof window !== 'undefined') {
      const p = window.location.pathname;
      if (
        p.includes('/grupos/espelho') ||
        p.includes('/groups/mirror') ||
        p.includes('/espelho')
      ) {
        return 'mirror';
      }
      if (p.includes('/grupos/monitor') || p.includes('/groups/monitor')) {
        return 'monitor';
      }
    }
    return 'list';
  });

  useEffect(() => {
    if (initialSubTab) {
      setSubTab(initialSubTab);
    }
  }, [initialSubTab]);

  useEffect(() => {
    const handlePopState = () => {
      const p = window.location.pathname;
      if (
        p.includes('/grupos/espelho') ||
        p.includes('/groups/mirror') ||
        p.includes('/espelho')
      ) {
        setSubTab('mirror');
      } else if (
        p.includes('/grupos/monitor') ||
        p.includes('/groups/monitor')
      ) {
        setSubTab('monitor');
      } else {
        setSubTab('list');
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Layout switcher: cards vs datatable
  const [viewMode, setViewMode] = useState<'cards' | 'table'>('cards');

  // Drawer form state (Create / Edit)
  const [isDrawerOpen, setIsDrawerOpen] = useState(initialDrawerOpen);
  const [editingGroup, setEditingGroup] = useState<WhatsAppGroup | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Form fields
  const [name, setName] = useState('');
  const [inviteLink, setInviteLink] = useState('');
  const [maxCapacity, setMaxCapacity] = useState(1000);
  const [currentMembers, setCurrentMembers] = useState(0);
  const [priority, setPriority] = useState(1);
  const [status, setStatus] = useState<
    'active' | 'standby' | 'overflow' | 'desynced'
  >('active');
  // Cadência e Janela Comercial do Grupo
  const [dispatchStartHour, setDispatchStartHour] = useState(8);
  const [dispatchEndHour, setDispatchEndHour] = useState(22);
  const [minDelayMinutes, setMinDelayMinutes] = useState(4);
  const [maxDelayMinutes, setMaxDelayMinutes] = useState(8);

  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  // Grupos aptos do chip WhatsApp (para seleção e auto-preenchimento no drawer)
  const [availableGroups, setAvailableGroups] = useState<
    AvailableWhatsAppGroup[]
  >([]);
  const [isLoadingAvailable, setIsLoadingAvailable] = useState(false);
  const [selectedAptJid, setSelectedAptJid] = useState('');
  const [selectedJid, setSelectedJid] = useState<string | null>(null);
  const [isResolvingLink, setIsResolvingLink] = useState(false);
  const [isAptCollapseOpen, setIsAptCollapseOpen] = useState(false);

  // Delete modal state
  const [deletingGroup, setDeletingGroup] = useState<WhatsAppGroup | null>(
    null
  );
  const [isDeleting, setIsDeleting] = useState(false);

  // Copy link feedback
  const [copiedGroupId, setCopiedGroupId] = useState<string | null>(null);

  // Group Details & Metrics Drawer state
  const [selectedMetricsGroup, setSelectedMetricsGroup] =
    useState<WhatsAppGroup | null>(null);

  // Search, Status Filter & Pagination state
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<
    'all' | 'active' | 'standby' | 'desynced' | 'no_permission'
  >('all');
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 9; // 9 cards por página (3x3 grid)

  // Clear all modal state
  const [isClearAllModalOpen, setIsClearAllModalOpen] = useState(false);
  const [isClearingAll, setIsClearingAll] = useState(false);
  const onGroupsChangeRef = useRef(onGroupsChange);
  useEffect(() => {
    onGroupsChangeRef.current = onGroupsChange;
  }, [onGroupsChange]);

  const loadGroups = useCallback(async () => {
    if (!token) return;
    setIsLoading(true);
    setLoadError(null);
    try {
      const data = await fetchGroups(token);
      setGroups(data);
      setLoadError(null);
      onGroupsChangeRef.current?.(data);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      setLoadError(msg);
      error(msg, 'Erro ao carregar grupos');
    } finally {
      setIsLoading(false);
    }
  }, [token, error]);

  useEffect(() => {
    loadGroups();
  }, [loadGroups]);

  const resetForm = () => {
    setName('');
    setInviteLink('');
    setMaxCapacity(1000);
    setCurrentMembers(0);
    setPriority(groups.length + 1);
    setStatus('standby');
    setDispatchStartHour(8);
    setDispatchEndHour(22);
    setMinDelayMinutes(4);
    setMaxDelayMinutes(8);
    setFormErrors({});
    setEditingGroup(null);
    setSelectedAptJid('');
    setSelectedJid(null);
    setIsAptCollapseOpen(false);
  };

  const openCreateDrawer = () => {
    resetForm();
    setIsDrawerOpen(true);
    if (token) {
      setIsLoadingAvailable(true);
      fetchAvailableGroups(token)
        .then((data) => setAvailableGroups(data))
        .catch(() => {})
        .finally(() => setIsLoadingAvailable(false));
    }
  };

  const handleSelectAptGroup = async (jid: string) => {
    setSelectedAptJid(jid);
    if (!jid) {
      setSelectedJid(null);
      return;
    }
    const found = availableGroups.find((g) => g.jid === jid);
    if (found) {
      setName(found.name);
      setCurrentMembers(found.current_members || 0);
      setSelectedJid(found.jid);
      setFormErrors((prev) => ({ ...prev, name: '', inviteLink: '' }));

      if (found.invite_link) {
        setInviteLink(found.invite_link);
      } else if (token) {
        setIsResolvingLink(true);
        try {
          const resolved = await resolveGroupJid(token, found.jid);
          if (resolved.invite_link) {
            setInviteLink(resolved.invite_link);
            setAvailableGroups((prev) =>
              prev.map((g) =>
                g.jid === found.jid
                  ? { ...g, invite_link: resolved.invite_link }
                  : g
              )
            );
            success(
              `Link oficial do grupo obtido via WhatsApp!`,
              'Link Conectado'
            );
          }
        } catch {
          // Permite preenchimento manual se não conseguir gerar automaticamente
        } finally {
          setIsResolvingLink(false);
        }
      }
    }
  };

  const handleResolveLink = async () => {
    if (!token || !inviteLink.trim()) return;
    setIsResolvingLink(true);
    try {
      const info = await resolveGroupLink(token, inviteLink.trim());
      if (info) {
        if (
          !name ||
          name === 'Grupo WhatsApp' ||
          name === 'Grupo VIP WhatsApp'
        ) {
          setName(info.name);
        }
        if (info.current_members > 0) {
          setCurrentMembers(info.current_members);
        }
        if (info.jid) {
          setSelectedJid(info.jid);
        }
        success(
          `Dados obtidos: "${info.name}" (${info.current_members} membros)`,
          'Link Verificado'
        );
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      info(msg, 'Aviso');
    } finally {
      setIsResolvingLink(false);
    }
  };

  const openEditDrawer = (group: WhatsAppGroup) => {
    setEditingGroup(group);
    setName(group.name);
    setInviteLink(group.invite_link);
    setMaxCapacity(group.max_capacity);
    setCurrentMembers(group.current_members);
    setPriority(group.priority);
    setStatus(group.status);
    setDispatchStartHour(group.dispatch_start_hour ?? 8);
    setDispatchEndHour(group.dispatch_end_hour ?? 22);
    setMinDelayMinutes(group.min_delay_minutes ?? 4);
    setMaxDelayMinutes(group.max_delay_minutes ?? 8);
    setSelectedJid(group.jid || null);
    setSelectedAptJid('');
    setFormErrors({});
    setIsDrawerOpen(true);
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};
    if (!name.trim()) errors.name = 'O nome do grupo é obrigatório.';
    if (!inviteLink.trim()) {
      errors.inviteLink = 'O link de convite é obrigatório.';
    } else if (
      !inviteLink.includes('chat.whatsapp.com/') &&
      !inviteLink.includes('wa.me/')
    ) {
      errors.inviteLink =
        'Informe um link válido do WhatsApp (ex: https://chat.whatsapp.com/...).';
    }
    if (maxCapacity < 10 || maxCapacity > 1024) {
      errors.maxCapacity = 'A capacidade deve estar entre 10 e 1.024 membros.';
    }
    if (currentMembers < 0) {
      errors.currentMembers = 'A contagem de membros não pode ser negativa.';
    }
    if (priority < 1) {
      errors.priority = 'A prioridade deve ser no mínimo 1.';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSaveGroup = async () => {
    if (!token || !validateForm()) return;

    setIsSubmitting(true);
    try {
      const payload: WhatsAppGroupCreatePayload = {
        name: name.trim(),
        invite_link: inviteLink.trim(),
        max_capacity: Number(maxCapacity),
        current_members: Number(currentMembers),
        priority: Number(priority),
        status,
        jid: selectedJid,
        dispatch_start_hour: Number(dispatchStartHour),
        dispatch_end_hour: Number(dispatchEndHour),
        min_delay_minutes: Number(minDelayMinutes),
        max_delay_minutes: Number(maxDelayMinutes),
      };

      if (editingGroup) {
        await updateGroup(token, editingGroup.id, payload);
        success(`Grupo "${name}" atualizado com sucesso.`, 'Grupo Salvo');
      } else {
        await createGroup(token, payload);
        success(`Grupo "${name}" cadastrado com sucesso.`, 'Grupo Criado');
      }

      setIsDrawerOpen(false);
      resetForm();
      await loadGroups();
      window.dispatchEvent(new CustomEvent('operational-health-refresh'));
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      error(msg, 'Falha ao salvar grupo');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteGroup = async () => {
    if (!token || !deletingGroup) return;
    setIsDeleting(true);
    try {
      await deleteGroup(token, deletingGroup.id);
      success(`Grupo "${deletingGroup.name}" excluído.`, 'Grupo Removido');
      setDeletingGroup(null);
      await loadGroups();
      window.dispatchEvent(new CustomEvent('operational-health-refresh'));
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      error(msg, 'Erro ao excluir grupo');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleSyncGroup = async (groupId: string) => {
    if (!token) return;
    setSyncingGroupId(groupId);
    try {
      const res = await syncGroup(token, groupId);
      if (res.synced) {
        const namePart = res.name ? `"${res.name}": ` : '';
        success(
          `${namePart}${res.current_members} membros (${res.status === 'overflow' ? 'Lotado' : 'OK'}).`,
          'Sincronização Concluída'
        );
      } else {
        info(res.message, 'Aviso de Sincronização');
      }
      await loadGroups();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      error(msg, 'Falha ao sincronizar');
    } finally {
      setSyncingGroupId(null);
    }
  };

  const handleSyncAll = async () => {
    if (!token) return;
    setIsSyncingAll(true);
    try {
      const res = await syncAllGroups(token);
      success(res.message, 'Sincronização Geral');
      await loadGroups();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      error(msg, 'Erro na sincronização geral');
    } finally {
      setIsSyncingAll(false);
    }
  };

  const handleClearAllGroups = async () => {
    if (!token) return;
    setIsClearingAll(true);
    try {
      const res = await clearAllGroups(token);
      success(res.message, 'Grupos Removidos');
      setIsClearAllModalOpen(false);
      await loadGroups();
      window.dispatchEvent(new CustomEvent('operational-health-refresh'));
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      error(msg, 'Erro ao limpar grupos');
    } finally {
      setIsClearingAll(false);
    }
  };

  const handleRelinkGroup = async (groupId: string) => {
    if (!token) return;
    try {
      const updated = await relinkGroup(token, groupId);
      success(
        `Grupo "${updated.name}" vinculado à linha atual (${updated.phone_number || 'Conectada'}).`,
        'Linha Vinculada'
      );
      await loadGroups();
      window.dispatchEvent(new CustomEvent('operational-health-refresh'));
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      error(msg, 'Erro ao vincular linha');
    }
  };

  const handleCopyLink = (link: string, id: string) => {
    navigator.clipboard.writeText(link);
    setCopiedGroupId(id);
    success('Link de convite copiado!', 'Copiado');
    setTimeout(() => setCopiedGroupId(null), 2500);
  };

  const handleToggleGroupStatus = async (group: WhatsAppGroup) => {
    if (!token) return;
    if (group.can_send === false && group.status !== 'active') {
      error(
        group.permission_error ||
          'Este grupo não permite mensagens de não-administradores.',
        'Sem Permissão'
      );
      return;
    }
    const nextStatus = group.status === 'active' ? 'standby' : 'active';
    setTogglingGroupId(group.id);
    try {
      await updateGroup(token, group.id, { status: nextStatus });
      success(
        `Grupo "${group.name}" agora está ${nextStatus === 'active' ? 'Ativo' : 'em Standby'}.`,
        nextStatus === 'active' ? 'Grupo Ativado' : 'Grupo Desativado'
      );
      await loadGroups();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      error(msg, 'Erro ao alterar status');
    } finally {
      setTogglingGroupId(null);
    }
  };

  // DataTable columns configuration
  const columns: Column<WhatsAppGroup>[] = [
    {
      key: 'name',
      header: 'Grupo',
      render: (item) => (
        <div className="min-w-0 max-w-xs space-y-0.5">
          <Tooltip
            content={item.name}
            position="bottom"
            className="block w-full min-w-0"
          >
            <span className="block truncate font-semibold text-content-main">
              {item.name}
            </span>
          </Tooltip>
          <span className="block truncate font-mono text-2xs text-content-muted">
            {item.invite_link}
          </span>
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (item) => {
        const isDesynced =
          item.status === 'desynced' ||
          (!isWhatsAppConnected && item.status === 'active');
        if (isDesynced) {
          return (
            <Badge variant="warning" withDot>
              Dessincronizado
            </Badge>
          );
        }
        const hasNoPermission = item.can_send === false;
        if (hasNoPermission && !isDesynced) {
          return (
            <Tooltip
              content={
                item.permission_error ||
                'Apenas administradores podem enviar mensagens neste grupo fechado.'
              }
              position="bottom"
            >
              <span>
                <Badge variant="warning" withDot>
                  Sem Permissão
                </Badge>
              </span>
            </Tooltip>
          );
        }

        return (
          <Badge
            variant={
              item.status === 'active'
                ? 'online'
                : item.status === 'overflow'
                  ? 'warning'
                  : 'neutral'
            }
            withDot={item.status !== 'standby'}
            pulse={item.status === 'active'}
          >
            {item.status === 'active'
              ? 'Ativo'
              : item.status === 'overflow'
                ? 'Lotado'
                : 'Standby'}
          </Badge>
        );
      },
    },
    {
      key: 'priority',
      header: 'Prioridade',
      className: 'text-center w-24',
      render: (item) => (
        <span className="font-mono text-xs font-semibold text-content-main">
          #{item.priority}
        </span>
      ),
    },
    {
      key: 'capacity',
      header: 'Capacidade & Lotação',
      render: (item) => {
        const percent = Math.min(
          Math.round((item.current_members / item.max_capacity) * 100),
          100
        );
        return (
          <div className="w-36 space-y-1">
            <div className="flex items-center justify-between font-mono text-2xs text-content-muted">
              <span>{item.current_members}</span>
              <span>{percent}%</span>
              <span>{item.max_capacity}</span>
            </div>
            <div className="h-1.5 w-full overflow-hidden rounded-full border border-surface-border bg-surface-subtle">
              <div
                className={`h-full rounded-full transition-all duration-300 ${
                  percent >= 90
                    ? 'bg-red-500'
                    : percent >= 70
                      ? 'bg-amber-500'
                      : 'bg-brand-500'
                }`}
                style={{ width: `${percent}%` }}
              />
            </div>
          </div>
        );
      },
    },
    {
      key: 'phone_number',
      header: 'Linha / Chip',
      render: (item) => (
        <div className="space-y-0.5">
          <span className="font-mono text-2xs text-content-main">
            {item.phone_number || 'Não vinculada'}
          </span>
          <div className="flex items-center gap-1.5 text-micro text-content-muted">
            <span>{item.total_clicks || 0} cliques</span>
            <span>•</span>
            <span>{item.messages_sent || 0} envios</span>
          </div>
        </div>
      ),
    },
    {
      key: 'actions',
      header: 'Ações',
      className: 'text-right w-48',
      render: (item) => {
        const isDesynced =
          item.status === 'desynced' ||
          (!isWhatsAppConnected && item.status === 'active');
        const isToggleDisabled =
          togglingGroupId === item.id ||
          (item.can_send === false && item.status !== 'active');
        const isActive = item.status === 'active' && !isDesynced;

        return (
          <div className="flex items-center justify-end gap-1.5">
            <Tooltip
              content={
                item.can_send === false && item.status !== 'active'
                  ? item.permission_error ||
                    'Chip não é admin neste grupo fechado (sem permissão de envio)'
                  : isActive
                    ? 'Desativar grupo (Standby)'
                    : 'Ativar grupo para disparos'
              }
            >
              <button
                type="button"
                role="switch"
                aria-checked={isActive}
                disabled={isToggleDisabled}
                onClick={() => handleToggleGroupStatus(item)}
                aria-label={
                  isActive ? `Desativar ${item.name}` : `Ativar ${item.name}`
                }
                className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 ${
                  isToggleDisabled
                    ? 'cursor-not-allowed bg-surface-subtle opacity-40'
                    : isActive
                      ? 'bg-emerald-600'
                      : 'bg-surface-subtle hover:bg-surface-border'
                }`}
              >
                <span
                  className={`pointer-events-none inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out ${
                    isActive ? 'translate-x-4' : 'translate-x-0'
                  }`}
                />
              </button>
            </Tooltip>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSelectedMetricsGroup(item)}
              title="Ver telemetria e métricas"
              className="text-brand-600 dark:text-brand-400"
            >
              <BarChart2 className="h-3.5 w-3.5" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              isLoading={syncingGroupId === item.id}
              onClick={() => handleSyncGroup(item.id)}
              title="Sincronizar membros e nome"
            >
              <RefreshCw
                className={`h-3.5 w-3.5 ${syncingGroupId === item.id ? 'animate-spin' : ''}`}
              />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => openEditDrawer(item)}
              title="Editar grupo"
            >
              <Edit2 className="h-3.5 w-3.5" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setDeletingGroup(item)}
              title="Excluir grupo"
              className="text-content-muted hover:text-red-600 dark:hover:text-red-400"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        );
      },
    },
  ];

  // Filtragem reativa por texto e status
  const filteredGroups = groups.filter((g) => {
    const isDesynced =
      g.status === 'desynced' ||
      (!isWhatsAppConnected && g.status === 'active');

    // Filtro de status
    if (statusFilter === 'active') {
      if (isDesynced || g.status !== 'active') return false;
    } else if (statusFilter === 'standby') {
      if (g.status !== 'standby') return false;
    } else if (statusFilter === 'desynced') {
      if (!isDesynced) return false;
    } else if (statusFilter === 'no_permission') {
      // Sem permissão: chip participa mas não pode enviar mensagens (exclui os dessincronizados)
      if (isDesynced || g.can_send !== false) return false;
    }

    // Filtro de busca por nome ou número/JID
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      const matchName = g.name.toLowerCase().includes(term);
      const matchPhone = (g.phone_number || '').toLowerCase().includes(term);
      const matchJid = (g.jid || '').toLowerCase().includes(term);
      if (!matchName && !matchPhone && !matchJid) return false;
    }

    return true;
  });

  const totalPages = Math.max(1, Math.ceil(filteredGroups.length / pageSize));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const paginatedGroups = filteredGroups.slice(
    (safeCurrentPage - 1) * pageSize,
    safeCurrentPage * pageSize
  );

  if (subTab === 'mirror') {
    return (
      <div className="space-y-6">
        <MirrorGroupView
          onNavigateToGroups={() => {
            setSubTab('list');
            if (window.location.pathname !== '/grupos') {
              window.history.pushState(null, '', '/grupos');
              window.dispatchEvent(new PopStateEvent('popstate'));
            }
          }}
        />
      </div>
    );
  }

  if (subTab === 'monitor') {
    return (
      <div className="space-y-6">
        <GroupMonitorView
          groups={groups}
          onNavigateToGroups={() => {
            setSubTab('list');
            if (window.location.pathname !== '/grupos') {
              window.history.pushState(null, '', '/grupos');
              window.dispatchEvent(new PopStateEvent('popstate'));
            }
          }}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* 1. PageHeader Estrutural do Design System */}
      <PageHeader
        title="Grupos"
        subtitle="Gerenciamento de grupos do WhatsApp para envio de promoções"
        actions={
          <>
            {groups.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                className="text-content-muted hover:text-red-600 dark:hover:text-red-400"
                leftIcon={<Trash2 className="h-3.5 w-3.5" />}
                onClick={() => setIsClearAllModalOpen(true)}
                title="Limpar todos os grupos do sistema (não afeta o WhatsApp)"
              >
                Limpar Todos
              </Button>
            )}
            <Button
              variant="secondary"
              size="sm"
              isLoading={isSyncingAll}
              leftIcon={
                <RefreshCw
                  className={`h-3.5 w-3.5 ${isSyncingAll ? 'animate-spin' : ''}`}
                />
              }
              onClick={handleSyncAll}
              disabled={groups.length === 0}
            >
              Sincronizar Todos
            </Button>
            <Button
              variant="primary"
              size="sm"
              leftIcon={<Plus className="h-3.5 w-3.5" />}
              onClick={openCreateDrawer}
            >
              Novo Grupo
            </Button>
          </>
        }
      />

      {/* 3. Controles de Visualização, Filtros & Lista de Grupos */}
      <div className="space-y-4">
        {/* Barra Superior: Busca, Filtros de Status e Switcher de Visualização */}
        <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-center">
          {/* Abas de Status com Contadores */}
          <div className="flex items-center gap-1 overflow-x-auto rounded-xl border border-surface-border bg-surface-subtle p-1">
            {[
              { id: 'all', label: 'Todos', count: groups.length },
              {
                id: 'active',
                label: 'Ativos',
                count: groups.filter((g) => {
                  const isDesynced =
                    g.status === 'desynced' ||
                    (!isWhatsAppConnected && g.status === 'active');
                  return g.status === 'active' && !isDesynced;
                }).length,
              },
              {
                id: 'standby',
                label: 'Standby',
                count: groups.filter((g) => g.status === 'standby').length,
              },
              {
                id: 'no_permission',
                label: 'Sem Permissão',
                count: groups.filter((g) => {
                  const isDesynced =
                    g.status === 'desynced' ||
                    (!isWhatsAppConnected && g.status === 'active');
                  return !isDesynced && g.can_send === false;
                }).length,
              },
              {
                id: 'desynced',
                label: 'Dessincronizados',
                count: groups.filter(
                  (g) =>
                    g.status === 'desynced' ||
                    (!isWhatsAppConnected && g.status === 'active')
                ).length,
              },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => {
                  setStatusFilter(tab.id as typeof statusFilter);
                  setCurrentPage(1);
                }}
                className={`flex cursor-pointer items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium transition-colors ${
                  statusFilter === tab.id
                    ? 'shadow-xs bg-surface-panel font-semibold text-content-main'
                    : 'text-content-muted hover:text-content-main'
                }`}
              >
                <span>{tab.label}</span>
                {typeof tab.count === 'number' && tab.count > 0 && (
                  <span
                    className={`py-0.2 rounded-full px-1.5 text-micro font-bold ${
                      tab.id === 'no_permission'
                        ? 'bg-amber-500/10 text-amber-600 dark:text-amber-400'
                        : tab.id === 'desynced'
                          ? 'bg-red-500/10 text-red-600 dark:text-red-400'
                          : 'bg-surface-subtle text-content-muted'
                    }`}
                  >
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Campo de Busca e Alternador de Layout */}
          <div className="flex items-center gap-2">
            <div className="w-48 sm:w-64">
              <Input
                placeholder="Buscar grupo ou chip..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                leftIcon={<Search className="h-3.5 w-3.5 text-content-muted" />}
              />
            </div>

            <div className="flex items-center gap-1 rounded-xl border border-surface-border bg-surface-subtle p-1">
              <button
                onClick={() => setViewMode('cards')}
                title="Visualização em Cards"
                className={`cursor-pointer rounded-lg p-1.5 text-xs transition-colors ${
                  viewMode === 'cards'
                    ? 'shadow-xs bg-surface-panel font-medium text-content-main'
                    : 'text-content-muted hover:text-content-main'
                }`}
              >
                <LayoutGrid className="h-4 w-4" />
              </button>
              <button
                onClick={() => setViewMode('table')}
                title="Visualização em Tabela"
                className={`cursor-pointer rounded-lg p-1.5 text-xs transition-colors ${
                  viewMode === 'table'
                    ? 'shadow-xs bg-surface-panel font-medium text-content-main'
                    : 'text-content-muted hover:text-content-main'
                }`}
              >
                <List className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Estados de Carregamento, Erro ou Conteúdo */}
        {isLoading && groups.length === 0 ? (
          <LoadingSkeleton variant="table" count={4} />
        ) : loadError && groups.length === 0 ? (
          <Card>
            <Card.Body className="mx-auto max-w-md space-y-4 px-4 py-12 text-center">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl border border-red-500/20 bg-red-500/10 text-red-600 dark:text-red-400">
                <AlertCircle className="h-6 w-6" />
              </div>
              <div className="space-y-1">
                <h4 className="text-sm font-semibold text-content-main">
                  Não foi possível recuperar os dados da API
                </h4>
                <p className="text-xs leading-relaxed text-content-muted">
                  Ocorreu uma falha ao carregar seus grupos de WhatsApp. Por
                  favor, tente novamente.
                </p>
              </div>
              <Button
                variant="primary"
                size="sm"
                leftIcon={<RefreshCw className="h-3.5 w-3.5" />}
                onClick={loadGroups}
              >
                Tentar Novamente
              </Button>
            </Card.Body>
          </Card>
        ) : groups.length === 0 && !isLoading ? (
          <Card>
            <Card.Body className="mx-auto max-w-md space-y-4 px-4 py-12 text-center">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl border border-brand-500/20 bg-brand-500/10 text-brand-600 dark:text-brand-400">
                <Users className="h-6 w-6" />
              </div>
              <div className="space-y-1">
                <h4 className="text-sm font-semibold text-content-main">
                  Nenhum Grupo Cadastrado
                </h4>
                <p className="text-xs leading-relaxed text-content-muted">
                  Cadastre seus links de convite do WhatsApp para ativar o
                  monitoramento automático de lotação e o transbordo para novas
                  vagas.
                </p>
              </div>
              <Button
                variant="primary"
                size="sm"
                leftIcon={<Plus className="h-3.5 w-3.5" />}
                onClick={openCreateDrawer}
              >
                Cadastrar Primeiro Grupo
              </Button>
            </Card.Body>
          </Card>
        ) : viewMode === 'cards' ? (
          /* Grid de Cards Responsivo com Paginação */
          filteredGroups.length === 0 ? (
            <Card>
              <Card.Body className="mx-auto max-w-md space-y-3 px-4 py-8 text-center">
                <p className="text-xs text-content-muted">
                  Nenhum grupo encontrado com os filtros aplicados.
                </p>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setSearchTerm('');
                    setStatusFilter('all');
                    setCurrentPage(1);
                  }}
                >
                  Limpar Filtros
                </Button>
              </Card.Body>
            </Card>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                {paginatedGroups.map((group) => {
                  const percent = Math.min(
                    Math.round(
                      (group.current_members / group.max_capacity) * 100
                    ),
                    100
                  );
                  const isDesynced =
                    group.status === 'desynced' ||
                    (!isWhatsAppConnected && group.status === 'active');
                  const isOverflow =
                    group.status === 'overflow' || percent >= 100;
                  const isActive = group.status === 'active' && !isDesynced;
                  const hasNoPermission = group.can_send === false;

                  return (
                    <Card
                      key={group.id}
                      className="flex flex-col justify-between"
                    >
                      <Card.Header
                        title={
                          <div className="min-w-0 space-y-1">
                            <Tooltip
                              content={group.name}
                              position="bottom"
                              className="block w-full min-w-0"
                            >
                              <span className="block truncate text-sm font-semibold text-content-main">
                                {group.name}
                              </span>
                            </Tooltip>
                            <div className="flex flex-wrap items-center gap-1.5">
                              {group.phone_number ? (
                                <span className="flex items-center gap-1 font-mono text-2xs text-content-muted">
                                  <Smartphone className="h-3 w-3" />
                                  {group.phone_number}
                                </span>
                              ) : (
                                <span className="text-2xs text-amber-600 dark:text-amber-400">
                                  Sem chip vinculado
                                </span>
                              )}
                            </div>
                          </div>
                        }
                        action={
                          <div className="flex items-center gap-1.5">
                            <span className="rounded-md border border-surface-border bg-surface-subtle px-2 py-0.5 font-mono text-2xs font-medium text-content-muted">
                              #{group.priority}
                            </span>
                            {hasNoPermission && !isDesynced ? (
                              <Tooltip
                                content={
                                  group.permission_error ||
                                  'Apenas administradores podem enviar mensagens neste grupo fechado.'
                                }
                                position="bottom"
                              >
                                <span>
                                  <Badge variant="warning" withDot>
                                    Sem Permissão
                                  </Badge>
                                </span>
                              </Tooltip>
                            ) : (
                              <Badge
                                variant={
                                  isDesynced
                                    ? 'warning'
                                    : isActive
                                      ? 'online'
                                      : isOverflow
                                        ? 'warning'
                                        : 'neutral'
                                }
                                withDot={!isOverflow || isDesynced}
                                pulse={isActive}
                              >
                                {isDesynced
                                  ? 'Dessincronizado'
                                  : isActive
                                    ? 'Ativo'
                                    : isOverflow
                                      ? 'Lotado'
                                      : 'Standby'}
                              </Badge>
                            )}
                          </div>
                        }
                      />

                      <Card.Body className="space-y-4">
                        {/* Barra de Progresso de Lotação */}
                        <div className="space-y-1.5">
                          <div className="flex items-center justify-between text-xs">
                            <span className="text-micro font-medium text-content-muted">
                              Lotação do Grupo
                            </span>
                            <span className="font-mono text-xs font-semibold text-content-main">
                              {group.current_members} / {group.max_capacity} (
                              {percent}%)
                            </span>
                          </div>
                          <div className="h-2 w-full overflow-hidden rounded-full border border-surface-border bg-surface-subtle">
                            <div
                              className={`h-full rounded-full transition-all duration-500 ${
                                percent >= 90
                                  ? 'bg-red-500'
                                  : percent >= 70
                                    ? 'bg-amber-500'
                                    : 'bg-brand-500'
                              }`}
                              style={{ width: `${percent}%` }}
                            />
                          </div>
                        </div>

                        {/* Telemetria Rápida: Cliques, Envios e Cadência */}
                        <div className="flex flex-col gap-1.5 rounded-lg border border-surface-border bg-surface-subtle px-2.5 py-1.5 text-2xs">
                          <div className="flex items-center justify-between">
                            <span className="flex items-center gap-1 text-content-muted">
                              <MousePointerClick className="h-3 w-3" />
                              <strong className="text-content-main">
                                {group.total_clicks || 0}
                              </strong>{' '}
                              cliques SubID
                            </span>
                            <span className="flex items-center gap-1 text-content-muted">
                              <Send className="h-3 w-3" />
                              <strong className="text-content-main">
                                {group.messages_sent || 0}
                              </strong>{' '}
                              envios
                            </span>
                          </div>
                          <div className="border-surface-border/60 flex items-center justify-between border-t pt-1 text-micro text-content-muted">
                            <span className="inline-flex items-center gap-1 font-mono">
                              <Clock className="h-2.5 w-2.5" />
                              {String(group.dispatch_start_hour ?? 8).padStart(
                                2,
                                '0'
                              )}
                              h -{' '}
                              {String(group.dispatch_end_hour ?? 22).padStart(
                                2,
                                '0'
                              )}
                              h
                            </span>
                            <span className="font-mono">
                              Cadência: {group.min_delay_minutes ?? 4}-
                              {group.max_delay_minutes ?? 8} min
                            </span>
                          </div>
                        </div>

                        {/* Link de Convite Truncado com Botão de Cópia */}
                        <div className="flex items-center justify-between gap-2 rounded-xl border border-surface-border bg-surface-subtle p-2.5">
                          <div className="flex min-w-0 flex-1 items-center gap-2">
                            <Link2 className="h-3.5 w-3.5 flex-shrink-0 text-content-muted" />
                            <span className="block truncate font-mono text-2xs text-content-main">
                              {group.invite_link}
                            </span>
                          </div>
                          <div className="flex flex-shrink-0 items-center gap-1">
                            <button
                              onClick={() =>
                                handleCopyLink(group.invite_link, group.id)
                              }
                              title="Copiar link"
                              className="cursor-pointer rounded-md p-1 text-content-muted transition-colors hover:bg-surface-panel hover:text-content-main"
                            >
                              {copiedGroupId === group.id ? (
                                <Check className="h-3.5 w-3.5 text-brand-500" />
                              ) : (
                                <Copy className="h-3.5 w-3.5" />
                              )}
                            </button>
                            <a
                              href={group.invite_link}
                              target="_blank"
                              rel="noreferrer noopener"
                              title="Abrir no WhatsApp"
                              className="rounded-md p-1 text-content-muted transition-colors hover:bg-surface-panel hover:text-content-main"
                            >
                              <ExternalLink className="h-3.5 w-3.5" />
                            </a>
                          </div>
                        </div>

                        {/* Telemetria de Sincronização */}
                        <div className="flex items-center justify-between pt-1 text-2xs text-content-muted">
                          <span>
                            {group.last_synced_at
                              ? `Sincronizado: ${new Date(group.last_synced_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
                              : 'Nunca sincronizado'}
                          </span>
                          {group.jid && (
                            <span className="max-w-24 truncate font-mono opacity-75">
                              {group.jid.split('@')[0]}
                            </span>
                          )}
                        </div>
                      </Card.Body>

                      <Card.Footer className="flex items-center justify-between gap-2 pt-3">
                        <div className="flex items-center gap-2">
                          <Tooltip
                            content={
                              group.can_send === false &&
                              group.status !== 'active'
                                ? group.permission_error ||
                                  'Chip não é admin neste grupo fechado (sem permissão de envio)'
                                : isActive
                                  ? 'Desativar grupo (Standby)'
                                  : 'Ativar grupo para disparos'
                            }
                            position="top"
                          >
                            <button
                              type="button"
                              role="switch"
                              aria-checked={isActive}
                              disabled={
                                togglingGroupId === group.id ||
                                (group.can_send === false &&
                                  group.status !== 'active')
                              }
                              onClick={() => handleToggleGroupStatus(group)}
                              aria-label={
                                isActive
                                  ? `Desativar ${group.name}`
                                  : `Ativar ${group.name}`
                              }
                              className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 ${
                                togglingGroupId === group.id ||
                                (group.can_send === false &&
                                  group.status !== 'active')
                                  ? 'cursor-not-allowed bg-surface-subtle opacity-40'
                                  : isActive
                                    ? 'bg-emerald-600'
                                    : 'bg-surface-subtle hover:bg-surface-border'
                              }`}
                            >
                              <span
                                className={`pointer-events-none inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out ${
                                  isActive ? 'translate-x-4' : 'translate-x-0'
                                }`}
                              />
                            </button>
                          </Tooltip>
                          <Button
                            variant="secondary"
                            size="sm"
                            isLoading={syncingGroupId === group.id}
                            leftIcon={
                              <RefreshCw
                                className={`h-3.5 w-3.5 ${syncingGroupId === group.id ? 'animate-spin' : ''}`}
                              />
                            }
                            onClick={() => handleSyncGroup(group.id)}
                          >
                            Sincronizar
                          </Button>
                        </div>

                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setSelectedMetricsGroup(group)}
                            title="Ver telemetria e métricas"
                            className="text-brand-600 dark:text-brand-400"
                          >
                            <BarChart2 className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditDrawer(group)}
                            title="Editar"
                          >
                            <Edit2 className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setDeletingGroup(group)}
                            title="Excluir"
                            className="text-content-muted hover:text-red-600 dark:hover:text-red-400"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </Card.Footer>
                    </Card>
                  );
                })}
              </div>

              {/* Paginação do Grid de Cards */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between border-t border-surface-border pt-4">
                  <span className="text-xs text-content-muted">
                    Exibindo {paginatedGroups.length} de {filteredGroups.length}{' '}
                    grupos (Página {safeCurrentPage} de {totalPages})
                  </span>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      disabled={safeCurrentPage <= 1}
                      leftIcon={<ChevronLeft className="h-3.5 w-3.5" />}
                    >
                      Anterior
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() =>
                        setCurrentPage((p) => Math.min(totalPages, p + 1))
                      }
                      disabled={safeCurrentPage >= totalPages}
                      rightIcon={<ChevronRight className="h-3.5 w-3.5" />}
                    >
                      Próxima
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )
        ) : (
          /* Tabela de Grupos com Paginação */
          <DataTable
            columns={columns}
            data={filteredGroups}
            keyExtractor={(item) => item.id}
            emptyMessage="Nenhum grupo encontrado com os filtros aplicados."
            pageSize={10}
          />
        )}
      </div>

      {/* 4. Drawer Lateral para Cadastro e Edição (>3 campos conforme ADR-0009) */}
      <Drawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        title={editingGroup ? 'Editar Grupo' : 'Novo Grupo'}
        description={
          editingGroup
            ? 'Ajuste os dados e o status do seu grupo'
            : 'Cadastre ou vincule um grupo do WhatsApp para direcionar seus envios'
        }
        width="md"
        footer={
          <div className="flex w-full items-center justify-end gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsDrawerOpen(false)}
              disabled={isSubmitting}
            >
              Cancelar
            </Button>
            <Button
              variant="primary"
              size="sm"
              isLoading={isSubmitting}
              onClick={handleSaveGroup}
            >
              {editingGroup ? 'Salvar Alterações' : 'Cadastrar Grupo'}
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          {/* Seletor Retrátil (Collapse) de Grupos Existentes no WhatsApp */}
          {!editingGroup && (
            <div className="overflow-hidden rounded-xl border border-surface-border bg-surface-subtle transition-all">
              <button
                type="button"
                onClick={() => setIsAptCollapseOpen((prev) => !prev)}
                className="hover:bg-surface-panel/60 flex w-full cursor-pointer items-center justify-between px-3.5 py-2.5 text-left text-xs font-semibold text-content-main transition-colors"
              >
                <div className="flex items-center gap-2">
                  <Radio className="h-4 w-4 text-brand-500" />
                  <span>Importar do WhatsApp</span>
                  {isLoadingAvailable && (
                    <span className="flex items-center gap-1 text-2xs font-normal text-content-muted">
                      <RefreshCw className="h-3 w-3 animate-spin text-brand-500" />
                      Buscando no chip...
                    </span>
                  )}
                  {!isLoadingAvailable && availableGroups.length > 0 && (
                    <Badge variant="online" withDot={false}>
                      {
                        availableGroups.filter((g) => !g.already_imported)
                          .length
                      }{' '}
                      disponíveis
                    </Badge>
                  )}
                </div>
                <ChevronDown
                  className={`h-4 w-4 text-content-muted transition-transform duration-200 ${
                    isAptCollapseOpen ? 'rotate-180' : ''
                  }`}
                />
              </button>

              {isAptCollapseOpen && (
                <div className="border-surface-border/60 bg-surface-canvas/30 space-y-2 border-t p-3 pt-2.5">
                  {availableGroups.length > 0 ? (
                    <div className="space-y-1.5">
                      <select
                        value={selectedAptJid}
                        onChange={(e) => handleSelectAptGroup(e.target.value)}
                        className="w-full rounded-lg border border-surface-border bg-surface-panel px-3 py-2 text-xs text-content-main focus:border-brand-500 focus:outline-none"
                      >
                        <option value="">
                          -- Selecione um grupo do chip para preencher --
                        </option>
                        {availableGroups.map((g) => {
                          const isDisabled =
                            g.already_imported || g.can_send === false;
                          const reasonText = g.already_imported
                            ? '— Já cadastrado'
                            : g.can_send === false
                              ? '— Apenas admins (sem permissão de envio)'
                              : '';
                          return (
                            <option
                              key={g.jid}
                              value={g.jid}
                              disabled={isDisabled}
                            >
                              {g.name} ({g.current_members} membros){' '}
                              {reasonText}
                            </option>
                          );
                        })}
                      </select>
                      <span className="block text-2xs text-content-muted">
                        Ao escolher um grupo, o nome, participantes e link de
                        convite oficial são carregados automaticamente. Grupos
                        restritos a administradores ficam desabilitados.
                      </span>
                    </div>
                  ) : !isLoadingAvailable ? (
                    <p className="text-2xs text-content-muted">
                      Nenhum grupo participante detectado nesta linha do
                      WhatsApp. Preencha os campos abaixo para cadastrar
                      manualmente.
                    </p>
                  ) : null}
                </div>
              )}
            </div>
          )}

          <Input
            label="Nome do Grupo"
            placeholder="Ex: Achadinhos Tech 01"
            value={name}
            onChange={(e) => setName(e.target.value)}
            error={formErrors.name}
            helperText="Nome do grupo no WhatsApp. É sincronizado e atualizado automaticamente ao sincronizar."
          />

          <div className="space-y-1.5">
            <Input
              label="Link de Convite do WhatsApp"
              placeholder="https://chat.whatsapp.com/L1M2N3O4P5Q6..."
              value={inviteLink}
              onChange={(e) => setInviteLink(e.target.value)}
              error={formErrors.inviteLink}
              leftIcon={<Link2 className="h-4 w-4" />}
            />

            {isResolvingLink && (
              <div className="flex items-center gap-1.5 pt-0.5 text-2xs text-brand-500">
                <RefreshCw className="h-3 w-3 animate-spin" />
                Obtendo dados e link de convite oficial via WhatsApp...
              </div>
            )}

            {/* Aviso quando o chip não é admin e não foi possível extrair o link */}
            {selectedAptJid && !inviteLink && !isResolvingLink && (
              <div className="rounded-xl border border-amber-500/20 bg-amber-500/5 p-3 text-xs text-amber-700 dark:text-amber-400">
                <div className="flex items-start gap-2">
                  <Info className="mt-0.5 h-4 w-4 flex-shrink-0" />
                  <div>
                    <span className="font-semibold">
                      Link não extraído automaticamente:
                    </span>{' '}
                    <span>
                      O chip conectado não possui permissão de administrador
                      neste grupo no WhatsApp. Por segurança da plataforma,
                      apenas administradores podem gerar links de convite. Por
                      favor, copie e cole o link oficial acima.
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Confirmação quando o link oficial foi importado com sucesso */}
            {selectedAptJid && inviteLink && !isResolvingLink && (
              <div className="flex items-center gap-1.5 text-2xs font-medium text-emerald-600 dark:text-emerald-400">
                <Check className="h-3.5 w-3.5" />
                Link de convite oficial obtido automaticamente via WhatsApp!
              </div>
            )}

            {inviteLink.includes('chat.whatsapp.com/') && !isResolvingLink && (
              <div className="flex justify-end pt-1">
                <Button
                  variant="ghost"
                  size="sm"
                  isLoading={isResolvingLink}
                  onClick={handleResolveLink}
                  leftIcon={<RefreshCw className="h-3 w-3" />}
                >
                  Buscar dados do grupo pelo link
                </Button>
              </div>
            )}
          </div>

          {/* Status do Grupo: Toggle Limpo e Direto (Ativo vs Inativo) */}
          <div className="flex items-center justify-between rounded-xl border border-surface-border bg-surface-subtle p-3.5">
            <div>
              <span className="block text-xs font-semibold text-content-main">
                Status do Grupo
              </span>
              <span className="block text-2xs text-content-muted">
                {status === 'active'
                  ? 'Ativo para receber envios de promoções da fila'
                  : 'Pausado (não receberá disparos automáticos)'}
              </span>
            </div>
            <button
              type="button"
              onClick={() =>
                setStatus((prev) => (prev === 'active' ? 'standby' : 'active'))
              }
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                status === 'active' ? 'bg-brand-500' : 'bg-surface-border'
              }`}
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                  status === 'active' ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
          </div>
        </div>
      </Drawer>

      {/* 6. GroupMetricsDrawer para Telemetria e Detalhes do Grupo */}
      <GroupMetricsDrawer
        isOpen={Boolean(selectedMetricsGroup)}
        onClose={() => setSelectedMetricsGroup(null)}
        group={selectedMetricsGroup}
        token={token}
        onSyncMembers={handleSyncGroup}
        onRelinkGroup={handleRelinkGroup}
        onOpenEdit={(g) => {
          setSelectedMetricsGroup(null);
          openEditDrawer(g);
        }}
      />

      {/* 7. ConfirmationModal para Exclusão de Grupo Único (Não afeta o WhatsApp) */}
      <ConfirmationModal
        isOpen={Boolean(deletingGroup)}
        onClose={() => setDeletingGroup(null)}
        onConfirm={handleDeleteGroup}
        isLoading={isDeleting}
        title="Remover Grupo do Sistema?"
        variant="danger"
        confirmText="Sim, Remover do Sistema"
        cancelText="Cancelar"
        description={`Tem certeza que deseja remover o grupo "${deletingGroup?.name}" do TrafficForge? Atenção: O grupo real no WhatsApp e seus participantes permanecerão 100% intactos.`}
      />

      {/* 8. ConfirmationModal para Limpeza Total (Começar do Zero) */}
      <ConfirmationModal
        isOpen={isClearAllModalOpen}
        onClose={() => setIsClearAllModalOpen(false)}
        onConfirm={handleClearAllGroups}
        isLoading={isClearingAll}
        title="Limpar Todos os Grupos (Começar do Zero)?"
        variant="danger"
        confirmText="Sim, Limpar Todos os Grupos"
        cancelText="Cancelar"
        description={`Você está prestes a desvincular todos os ${groups.length} grupos cadastrados no TrafficForge. Atenção: Nenhum grupo será apagado do WhatsApp real. Seus membros e links reais continuam seguros.`}
      />
    </div>
  );
};
